import React from 'react';
import type { QuoteFormData, QuoteFormErrors } from '../../types/quote';
import {
  FiCheckCircle,
  FiUser,
  FiMapPin,
  FiTruck,
  FiPackage,
  FiCalendar,
  FiEdit2,
  FiImage,
  FiAlertCircle,
} from 'react-icons/fi';

interface StepConfirmationProps {
  data: QuoteFormData;
  errors: QuoteFormErrors;
  onChange: (field: keyof QuoteFormData, value: any) => void;
  onJumpToStep: (stepId: number) => void;
  isSubmitting: boolean;
  submitError?: string | null;
  onSubmit: (e: React.FormEvent) => void;
}

export const StepConfirmation: React.FC<StepConfirmationProps> = ({
  data,
  errors,
  onChange,
  onJumpToStep,
  isSubmitting,
  submitError,
  onSubmit,
}) => {
  return (
    <div className="space-y-6">
      <div className="border-b border-gray-100 pb-4">
        <span className="text-xs uppercase tracking-widest text-[#F55109] font-bold">
          Step 6 of 6
        </span>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] special-header mt-1">
          SECTION 6 — CONFIRMATION
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Please review the summary below and confirm your transportation request before submitting.
        </p>
      </div>

      {/* Review Summary Grid */}
      <div className="space-y-4">
        {/* Section 1 Review */}
        <div className="bg-gray-50/70 border border-gray-200/80 rounded-2xl p-4 sm:p-5 transition-all hover:border-gray-300">
          <div className="flex items-center justify-between pb-3 border-b border-gray-200">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#F55109]/10 text-[#F55109] flex items-center justify-center">
                <FiUser className="w-4 h-4" />
              </div>
              <h4 className="font-bold text-gray-900 text-sm sm:text-base">
                Customer Information
              </h4>
            </div>
            <button
              type="button"
              onClick={() => onJumpToStep(1)}
              className="text-xs font-semibold text-[#F55109] hover:text-[#d44304] flex items-center gap-1 bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 shadow-2xs hover:bg-gray-50 transition-colors"
            >
              <FiEdit2 className="w-3 h-3" />
              Edit
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-3 text-sm">
            <div>
              <span className="block text-xs text-gray-400 font-medium">Full Name</span>
              <p className="font-semibold text-gray-800">{data.fullName || '—'}</p>
            </div>
            <div>
              <span className="block text-xs text-gray-400 font-medium">Company Name</span>
              <p className="font-semibold text-gray-800">{data.companyName || '—'}</p>
            </div>
            <div>
              <span className="block text-xs text-gray-400 font-medium">Phone Number</span>
              <p className="font-semibold text-gray-800">{data.phoneNumber || '—'}</p>
            </div>
            <div>
              <span className="block text-xs text-gray-400 font-medium">Email Address</span>
              <p className="font-semibold text-gray-800 truncate">{data.email || '—'}</p>
            </div>
          </div>
        </div>

        {/* Section 2 & 3 Review: Route Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Pickup Details */}
          <div className="bg-gray-50/70 border border-gray-200/80 rounded-2xl p-4 sm:p-5 transition-all hover:border-gray-300">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                  <FiMapPin className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm sm:text-base">Pickup Details</h4>
              </div>
              <button
                type="button"
                onClick={() => onJumpToStep(2)}
                className="text-xs font-semibold text-[#F55109] hover:text-[#d44304] flex items-center gap-1 bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 shadow-2xs hover:bg-gray-50 transition-colors"
              >
                <FiEdit2 className="w-3 h-3" />
                Edit
              </button>
            </div>
            <div className="space-y-3 mt-3 text-sm">
              <div>
                <span className="block text-xs text-gray-400 font-medium">Pickup Address</span>
                <p className="font-semibold text-gray-800 leading-snug whitespace-pre-line">
                  {data.pickupAddress || '—'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-gray-200/60">
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Contact Person</span>
                  <p className="font-semibold text-gray-800">{data.pickupContactName || '—'}</p>
                </div>
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Contact Phone</span>
                  <p className="font-semibold text-gray-800">{data.pickupContactPhone || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Details */}
          <div className="bg-gray-50/70 border border-gray-200/80 rounded-2xl p-4 sm:p-5 transition-all hover:border-gray-300">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-600 flex items-center justify-center">
                  <FiTruck className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm sm:text-base">Delivery Details</h4>
              </div>
              <button
                type="button"
                onClick={() => onJumpToStep(3)}
                className="text-xs font-semibold text-[#F55109] hover:text-[#d44304] flex items-center gap-1 bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 shadow-2xs hover:bg-gray-50 transition-colors"
              >
                <FiEdit2 className="w-3 h-3" />
                Edit
              </button>
            </div>
            <div className="space-y-3 mt-3 text-sm">
              <div>
                <span className="block text-xs text-gray-400 font-medium">Delivery Address</span>
                <p className="font-semibold text-gray-800 leading-snug whitespace-pre-line">
                  {data.deliveryAddress || '—'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-gray-200/60">
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Receiver Name</span>
                  <p className="font-semibold text-gray-800">{data.receiverName || '—'}</p>
                </div>
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Receiver Phone</span>
                  <p className="font-semibold text-gray-800">{data.receiverPhoneNumber || '—'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4 & 5 Review: Items & Preference */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Product Details */}
          <div className="bg-gray-50/70 border border-gray-200/80 rounded-2xl p-4 sm:p-5 transition-all hover:border-gray-300">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center">
                  <FiPackage className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm sm:text-base">Product & Delivery</h4>
              </div>
              <button
                type="button"
                onClick={() => onJumpToStep(4)}
                className="text-xs font-semibold text-[#F55109] hover:text-[#d44304] flex items-center gap-1 bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 shadow-2xs hover:bg-gray-50 transition-colors"
              >
                <FiEdit2 className="w-3 h-3" />
                Edit
              </button>
            </div>
            <div className="space-y-3 mt-3 text-sm">
              <div>
                <span className="block text-xs text-gray-400 font-medium">What You Are Sending</span>
                <p className="font-semibold text-gray-800 whitespace-pre-line">
                  {data.whatAreYouSending || '—'}
                </p>
              </div>
              <div>
                <span className="block text-xs text-gray-400 font-medium">Uploaded Images</span>
                <p className="font-semibold text-gray-800 flex items-center gap-1.5">
                  <FiImage className="w-4 h-4 text-gray-500" />
                  {data.productImages && data.productImages.length > 0
                    ? `${data.productImages.length} image(s) attached`
                    : 'None attached (Optional)'}
                </p>
              </div>
              {data.specialDeliveryInstructions && (
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Special Instructions</span>
                  <p className="text-gray-700 italic text-xs">{data.specialDeliveryInstructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Preferences */}
          <div className="bg-gray-50/70 border border-gray-200/80 rounded-2xl p-4 sm:p-5 transition-all hover:border-gray-300">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-sky-500/10 text-sky-600 flex items-center justify-center">
                  <FiCalendar className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm sm:text-base">Delivery Preferences</h4>
              </div>
              <button
                type="button"
                onClick={() => onJumpToStep(5)}
                className="text-xs font-semibold text-[#F55109] hover:text-[#d44304] flex items-center gap-1 bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 shadow-2xs hover:bg-gray-50 transition-colors"
              >
                <FiEdit2 className="w-3 h-3" />
                Edit
              </button>
            </div>
            <div className="space-y-3 mt-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Pickup Date</span>
                  <p className="font-semibold text-gray-800">{data.preferredPickupDate || '—'}</p>
                </div>
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Pickup Time</span>
                  <p className="font-semibold text-gray-800">{data.preferredPickupTime || 'Flexible'}</p>
                </div>
              </div>
              <div>
                <span className="block text-xs text-gray-400 font-medium">Special Handling</span>
                <p className="font-semibold text-gray-800">
                  {data.specialHandling && data.specialHandling.length > 0
                    ? data.specialHandling.join(', ') +
                    (data.otherHandlingDetails ? ` (${data.otherHandlingDetails})` : '')
                    : 'Standard Handling'}
                </p>
              </div>
              {data.additionalHandlingInstructions && (
                <div>
                  <span className="block text-xs text-gray-400 font-medium">Handling Notes</span>
                  <p className="text-gray-700 italic text-xs">{data.additionalHandlingInstructions}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 6 — Customer Confirmation (MUI Checkbox - Required) */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <label
          htmlFor="customerConfirmation"
          className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${data.customerConfirmation
              ? 'border-emerald-500 bg-emerald-50/30'
              : errors.customerConfirmation
                ? 'border-red-400 bg-red-50/20 ring-2 ring-red-100'
                : 'border-gray-200 hover:border-gray-300 bg-gray-50/60'
            }`}
        >
          <input
            id="customerConfirmation"
            type="checkbox"
            checked={data.customerConfirmation}
            onChange={(e) => onChange('customerConfirmation', e.target.checked)}
            className="mt-1 w-5 h-5 accent-[#F55109] text-[#F55109] focus:ring-[#F55109] rounded border-gray-300 transition-all cursor-pointer"
            required
          />
          <div>
            <span className="block text-sm sm:text-base font-bold text-gray-900 leading-snug">
              Customer Confirmation <span className="text-[#F55109]">*</span>
            </span>
            <p className="text-sm text-gray-700 mt-1 font-medium">
              I confirm that the information provided is accurate and complete.
            </p>
          </div>
        </label>
        {errors.customerConfirmation && (
          <p className="text-red-500 text-xs font-semibold mt-2 flex items-center gap-1.5 pl-2">
            {errors.customerConfirmation}
          </p>
        )}
      </div>

      {/* Submission Error Banner */}
      {submitError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-3 shadow-xs">
          <FiAlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p className="font-bold text-red-800">Unable to submit quote request</p>
            <p className="text-xs text-red-600 leading-relaxed">{submitError}</p>
          </div>
        </div>
      )}

      {/* SUBMIT BUTTON */}
      <div className="pt-4 flex flex-col sm:flex-row items-center justify-end gap-4">
        <button
          type="button"
          disabled={isSubmitting}
          onClick={onSubmit}
          className="w-full sm:w-auto px-10 py-4 bg-[#F55109] hover:bg-[#d44304] text-white text-base font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Submitting Request...</span>
            </>
          ) : (
            <>
              <FiCheckCircle className="w-5 h-5 stroke-[2.5]" />
              <span>Submit Transportation Request</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
